import { useEffect, useState } from 'react';
import type { StatsBreakdownRow, StatsSummary } from '@koto-event/shared';
import { api, type EventRow } from '../lib/api.js';
import { reportDateJst, reportPeriodJst } from '../lib/report-period.js';

/**
 * 効果測定（管理画面フロー STEP 15-16）。
 *
 *   参加状況 / 当選状況 / 特典付与状況 / 店舗×ココデピット別
 *   LINE登録誘導数 / LINE登録後の特典付与数
 * を期間指定で表示する。店舗別は CocodePit の location（店舗名）ごと。
 * 特典の利用状況は連携先のシステムの情報のため、ここでは表示しない（docs/todo.txt T-13）。
 */
export function DashboardPage(): JSX.Element {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState(reportDateJst(monthAgo));
  const [to, setTo] = useState(reportDateJst(today));
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventError, setEventError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [byLocation, setByLocation] = useState<StatsBreakdownRow[]>([]);
  const [byCocodePit, setByCocodePit] = useState<StatsBreakdownRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const params = { ...reportPeriodJst(from, to), eventId: eventId || undefined };
      const [s, location, cocodePit] = await Promise.all([
        api.statsSummary(params),
        api.statsByLocation(params),
        api.statsByCocodePit(params),
      ]);
      setSummary(s);
      setByLocation(location.items);
      setByCocodePit(cocodePit.items);
    } catch (e) {
      setSummary(null);
      setByLocation([]);
      setByCocodePit([]);
      setError(e instanceof Error ? e.message : '集計の取得に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
    void api.listEvents().then(({ items }) => setEvents(items)).catch((e: unknown) => {
      setEventError(e instanceof Error ? e.message : 'イベントの取得に失敗しました');
    });
    // 初回のみ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let csvUrl: string | null = null;
  try {
    csvUrl = api.entriesCsvUrl({ ...reportPeriodJst(from, to), eventId: eventId || undefined });
  } catch {
    // 日付の消去中や逆転した期間では、不正な条件の CSV を要求しない。
  }

  return (
    <>
      <h1>効果測定</h1>
      <p className="page-desc">参加から当選・特典付与までを確認できます。期間は日本時間の暦日（0時〜24時）です。</p>

      <div className="card">
        <div className="row">
          <div>
            <label htmlFor="eventId">イベント</label>
            <select id="eventId" value={eventId} disabled={busy} onChange={(e) => setEventId(e.target.value)}>
              <option value="">すべてのイベント</option>
              {events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="from">開始日</label>
            <input id="from" type="date" value={from} disabled={busy} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label htmlFor="to">終了日</label>
            <input id="to" type="date" value={to} disabled={busy} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button type="button" className="btn" disabled={busy} onClick={() => void load()}>
              {busy ? '読み込み中…' : '表示'}
            </button>
            {csvUrl && <a className="btn btn--ghost" href={csvUrl} style={{ textDecoration: 'none' }}>
              CSV 出力
            </a>}
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {eventError && <p className="error">イベント一覧: {eventError}</p>}

      {summary && (
        <div className="grid">
          <Stat label="タッチ数" value={summary.touchCount} />
          <Stat label="参加数" value={summary.entryCount} />
          <Stat label="当選数（参加賞を除く）" value={summary.winCount} />
          <Stat label="特典付与数" value={summary.rewardGrantedCount} />
          <Stat label="LINE登録誘導数" value={summary.lineGuideCount} />
          <Stat label="LINE登録後の特典付与数" value={summary.rewardGrantedAfterLineRegistration} />
        </div>
      )}

      <h2>店舗別</h2>
      <BreakdownTable rows={byLocation} firstColumn="店舗" />

      <h2>ココデピット別</h2>
      <BreakdownTable rows={byCocodePit} firstColumn="店舗 / 設置場所 / 製造管理番号" />
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value.toLocaleString('ja-JP')}</div>
    </div>
  );
}

function BreakdownTable({
  rows,
  firstColumn,
}: {
  rows: StatsBreakdownRow[];
  firstColumn: string;
}): JSX.Element {
  if (rows.length === 0) return <p className="page-desc">データがありません。</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>{firstColumn}</th>
          <th className="num">タッチ</th>
          <th className="num">参加</th>
          <th className="num">当選</th>
          <th className="num">特典付与</th>
          <th className="num">LINE登録誘導</th>
          <th className="num">登録後付与</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td>{r.label}</td>
            <td className="num">{r.touchCount}</td>
            <td className="num">{r.entryCount}</td>
            <td className="num">{r.winCount}</td>
            <td className="num">{r.rewardGrantedCount}</td>
            <td className="num">{r.lineGuideCount}</td>
            <td className="num">{r.rewardGrantedAfterLineRegistration}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
